import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SelectionType } from '../../enums/SelectionType.enum';
import styles from './QuickView.module.scss';
import { useProductAssets, ModelName } from '../../hooks/useProductAssets';
import pdpStyles from '../../landing_pages/ProductDetailLander/ProductDetailPageDesktop.module.scss';
import { ProductControls } from '../../components/ProductControls';
import { ProductPerks } from '../../components/ProductPerks';
import { ProductPriceSummary } from '../../components/ProductPriceSummary';
import { sizeImage } from '../../util';
import { ImageLoader } from './ImageLoader';
import { Icon } from '../../icons/Icon';
import { useSetModalState } from '../../hooks/actions/useModalState';
import { ModalToggleState } from '../../models/actionStates';
import { DivButton } from '../../root/components/DivButton';
import { CardBody } from '../../components/CardBody';
import { CardFooter } from '../../components/CardFooter';
import { CardHeader } from '../../components/CardHeader';
import { useProductSelection } from '../../hooks/useProductSelection';
import { Link } from '../../root/components/Link';
import { uniqueFilterByProperty } from '../../sizing-tool/utils/array-helper';

interface Props {
  selectionKey: string;
}
const cardStyles = { maxWidth: 1155 };

export const QuickView: React.FC<Props> = ({ selectionKey }) => {
  const { product, color, variant } = useProductSelection(SelectionType.QuickView, selectionKey);
  const reviewsRef = useRef<HTMLDivElement>(null);
  const productAssets = useProductAssets(product);
  const [activeModel, setActiveModel] = useState<ModelName>(productAssets.modelPhotos?.[0]?.modelName);
  const setModalState = useSetModalState();

  useEffect(() => {
    setActiveModel(productAssets.modelPhotos?.[0]?.modelName);
  }, [productAssets]);

  const modelPhotos = useMemo(() => {
    return productAssets.modelPhotos.filter((p) => p.color === color && p.modelName === activeModel);
  }, [color, productAssets, activeModel]);

  const possibleModels = useMemo(() => {
    return productAssets.modelPhotos.filter((p) => p.color === color).filter(uniqueFilterByProperty((model) => model.modelName));
  }, [color, productAssets]);

  useEffect(() => {
    setActiveModel(possibleModels[0]?.modelName);
  }, [possibleModels]);

  return (
    <div className={styles.hlQuickView} id="hl-quick-view">
      <CardHeader inlineStyles={cardStyles}></CardHeader>
      <CardBody style={cardStyles}>
        <div className={styles.bodyContainer}>
          <DivButton onClick={() => setModalState({ toggleState: ModalToggleState.Closed })} className={styles.closeBtn} label="close">
            <Icon name="close" style={{ width: '12px', marginLeft: '8px' }} />
          </DivButton>
          <div className={styles.leftWrapper}>
            <div className={styles.top}>
              {modelPhotos.map((p, i) => (
                <div className={styles.imageBox} key={p.src}>
                  <ImageLoader className={pdpStyles.grid} alt={`${product.title} ${i}`} src={sizeImage(p.src, '800x')} />
                </div>
              ))}
              {modelPhotos.length < 4 &&
                productAssets.productFeaturePhotos.map((p, i) => (
                  <div className={styles.imageBox} key={p.src}>
                    <ImageLoader alt={`${product.title} Feature ${i}`} src={sizeImage(p.src, '800x')} />
                  </div>
                ))}
            </div>
          </div>
          <div className={styles.rightWrapper}>
            <ProductPriceSummary product={product} variant={variant} reviewsSectionRef={reviewsRef} color={color} />
            <ProductPerks product={product} />
            <ProductControls
              selectionType={SelectionType.QuickView}
              selectionKey={selectionKey}
              style={{ position: 'sticky', top: 0, paddingTop: 20, marginBottom: 5, backgroundColor: 'white', zIndex: 2 }}
            />
            <div className={styles.garmentDesc}>
              <div>GARMENT DESCRIPTION</div>
              <span className={styles.panelText}>{product.coreFeatures}</span>
            </div>
            <Link
              size={'large'}
              dataTrackLabel="Quick View Details"
              dataTrackLocation="Quick View Details"
              href={`/products/${product.handle}`}
              text="View More Details"
              buttonType="secondary"
            />
          </div>
        </div>
      </CardBody>
      <CardFooter inlineStyles={cardStyles} />
    </div>
  );
};

export default QuickView;
